/**
 * Surface 1 — Tax Collectors Master List (Phase 3 UI)
 * See /app/memory/design_tax_collectors_ui.md §3.1
 *
 * Feb 2026 — Redesigned to share the Jurisdiction Directory design
 * language across the Jurisdiction Management workspace. KPI cards,
 * unified header/filter/table styling, and humanized collector type
 * labels (third_party_agency → "Third-Party Agency") are surfaced.
 * Internal codes remain in the data layer and on edit forms only.
 *
 * Access: admin/jurisdiction_manager full CRUD; others read-only.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, Pencil, Trash2, X, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  listTaxCollectors, listTaxCollectorTypes,
  createTaxCollector, updateTaxCollector, deactivateTaxCollector,
} from '../utils/taxCollectorsApi';
import { humanizeCollectorType, collectorTypeBadgeClass } from '../utils/humanize';
import { formatUSPhone, isValidUSPhone, trimOrNull, trimStr } from '../utils/phoneFormat';
import { displayTitleCase } from '../utils/formatters';
import './JurisdictionDirectory.css';

const ROLES_WITH_WRITE = new Set(['admin', 'jurisdiction_manager']);
const KPI_PAGE_SIZE = 200; // API maximum (GET /api/tax-collectors page_size le=200)

const emptyDraft = () => ({
  display_name: '', legal_name: '', collector_type: '',
  make_checks_payable_to: '',
  phone: '', phone_ext: '', fax: '', email: '', website: '',
  mailing_address: '', physical_address: '', working_hours: '',
  // Default to PA — TitleFlow's current operational footprint. Field
  // remains editable for multi-state expansion (see Feb 2026 UX note).
  accepts_mail_away: false, states_served: ['PA'], notes: '', is_active: true,
});

function relTime(iso) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return '—'; }
}

function StatesPill({ states = [] }) {
  if (!states || states.length === 0) return <span style={{ fontSize: '12px', color: '#94a3b8' }}>—</span>;
  if (states.length <= 3) {
    return <span style={{ fontSize: '13px', color: '#334155' }}>{states.join(', ')}</span>;
  }
  const head = states.slice(0, 2).join(', ');
  return (
    <span
      style={{ fontSize: '13px', color: '#334155' }}
      title={states.join(', ')}
      data-testid="tax-collector-states-pill"
    >
      {head} +{states.length - 2}
    </span>
  );
}

export default function TaxCollectorsPage() {
  const role = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.role || '';
    } catch { return ''; }
  })();
  const canWrite = ROLES_WITH_WRITE.has(role);

  const [items, setItems] = useState([]);
  // Headline KPI dataset — fetched independently of the paginated/filtered
  // view so the four KPI cards stay stable while admins explore filters.
  // Dataset is small (<200 rows in practice); a single page_size=500 fetch
  // is cheaper than introducing a stats endpoint.
  const [kpiItems, setKpiItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [types, setTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter !== 'all') params.collector_type = typeFilter;
      if (activeFilter === 'active') params.active_only = true;
      if (activeFilter === 'inactive') params.active_only = false;
      const res = await listTaxCollectors(params);
      setItems(res.items || []);
      setPagination(res.pagination || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load collectors');
    } finally { setLoading(false); }
  }, [page, debouncedSearch, typeFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listTaxCollectorTypes().then(r => setTypes(r.types || [])).catch(() => {});
  }, []);

  // Headline KPI fetch — independent of filters. Re-runs after a save/
  // delete via the `kpiBump` tick so the totals stay accurate.
  // TAX-COLLECTORS-KPI-001: the API caps page_size at 200, so page through
  // the full dataset; on failure surface the error instead of falling back
  // to the visible page (which produced a misleading "25" total).
  const [kpiBump, setKpiBump] = useState(0);
  const [kpiError, setKpiError] = useState(false);
  const [kpiLoaded, setKpiLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        let pageNo = 1;
        let totalPages = 1;
        do {
          const res = await listTaxCollectors({ page: pageNo, page_size: KPI_PAGE_SIZE });
          all.push(...(res.items || []));
          totalPages = res.pagination?.total_pages || 1;
          pageNo += 1;
        } while (pageNo <= totalPages && pageNo <= 50);
        if (cancelled) return;
        setKpiItems(all);
        setKpiError(false);
      } catch (_) {
        if (cancelled) return;
        setKpiItems([]);
        setKpiError(true);
      } finally {
        if (!cancelled) setKpiLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [kpiBump]);

  const kpis = useMemo(() => {
    const dataset = kpiItems;
    const total = dataset.length;
    const active = dataset.filter(r => r.is_active).length;
    const inactive = total - active;
    const stateSet = new Set();
    dataset.forEach(r => (r.states_served || []).forEach(s => stateSet.add(String(s).toUpperCase())));
    return { total, active, inactive, statesCovered: stateSet.size };
  }, [kpiItems]);
  const kpiValue = (n) => (kpiError ? '—' : (kpiLoaded ? n : '…'));

  const openCreate = () => { setEditing(null); setDraft(emptyDraft()); setModalOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setDraft({ ...emptyDraft(), ...row, states_served: row.states_served || [] });
    setModalOpen(true);
  };

  // ── Field handlers (Feb 2026 data hygiene) ──────────────────────
  // Trim text on blur so users see the cleaned value immediately.
  // Backend re-validates on save so the DB is the source of truth.
  const handleBlurTrim = (field) => (e) => {
    const trimmed = trimStr(e.target.value);
    if (trimmed !== e.target.value) {
      setDraft(d => ({ ...d, [field]: trimmed }));
    }
  };
  // Trim + uppercase on blur — used for the 5 structured fields that
  // follow the Jurisdiction Directory uppercase presentation standard.
  // Backend mirrors via `TaxCollectorBase._UPPERCASE_FIELDS`.
  const handleBlurUpper = (field) => (e) => {
    const v = e.target.value;
    const normalized = typeof v === 'string' ? v.trim().toUpperCase() : v;
    if (normalized !== v) {
      setDraft(d => ({ ...d, [field]: normalized }));
    }
  };
  // Phone / Fax: live-format every keystroke to XXX-XXX-XXXX so users
  // never have to type dashes themselves. Non-digits are stripped.
  const handlePhoneChange = (field) => (e) => {
    setDraft(d => ({ ...d, [field]: formatUSPhone(e.target.value) }));
  };

  // Inline validation — empty is OK, 10 digits is OK, anything in between
  // is flagged. Errors block Save and render a small red helper under the
  // affected field.
  const phoneError = draft.phone && !isValidUSPhone(draft.phone)
    ? 'Phone must be 10 digits' : '';
  const faxError = draft.fax && !isValidUSPhone(draft.fax)
    ? 'Fax must be 10 digits' : '';
  // Required-field errors (Feb 2026 promotion). Empty-after-trim is
  // treated the same as missing so a whitespace blob can't pass.
  const payableError = !(draft.make_checks_payable_to || '').trim()
    ? 'Make Checks Payable To is required' : '';
  const physicalError = !(draft.physical_address || '').trim() ||
    (draft.physical_address || '').trim().length < 5
    ? 'Physical Address is required (≥ 5 characters)' : '';

  const onSave = async () => {
    if (!draft.display_name?.trim() || !draft.collector_type) {
      toast.error('Display name and Type are required');
      return;
    }
    if (payableError || physicalError) {
      toast.error(payableError || physicalError);
      return;
    }
    if (phoneError || faxError) {
      toast.error(phoneError || faxError);
      return;
    }
    // Defense in depth — trim every text field client-side before the
    // request. Backend Pydantic validators do the same (and uppercase
    // the 5 structured fields) so legacy paths and direct API callers
    // also stay clean.
    const TEXT_FIELDS = [
      'display_name', 'legal_name', 'make_checks_payable_to',
      'email', 'website', 'mailing_address', 'physical_address',
      'working_hours', 'notes', 'phone_ext', 'request_instructions',
    ];
    const payload = { ...draft };
    TEXT_FIELDS.forEach(f => {
      if (f in payload) payload[f] = trimOrNull(payload[f]);
    });
    payload.phone = trimOrNull(payload.phone);
    payload.fax = trimOrNull(payload.fax);
    // Required check after trim — display_name + the two newly-required
    // operational fields.
    if (!payload.display_name) {
      toast.error('Display name is required');
      return;
    }
    if (!payload.make_checks_payable_to) {
      toast.error('Make Checks Payable To is required');
      return;
    }
    if (!payload.physical_address) {
      toast.error('Physical Address is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTaxCollector(editing.id, payload);
        toast.success('Collector updated');
      } else {
        await createTaxCollector(payload);
        toast.success('Collector created');
      }
      setModalOpen(false);
      load();
      setKpiBump(b => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await deactivateTaxCollector(deleteTarget.id);
      toast.success(r.deactivated ? `Deactivated (used by ${r.in_use_by})` : 'Deleted');
      setDeleteTarget(null);
      load();
      setKpiBump(b => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const noResults = !loading && items.length === 0;
  const hasFilters = !!search || typeFilter !== 'all' || activeFilter !== 'all';
  const resetFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setActiveFilter('all');
    setPage(1);
  };

  return (
    <div className="jurisdiction-directory jx-premium" data-testid="tax-collectors-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <span className="jx-head-icon" aria-hidden="true"><Users /></span>
          <div>
          <h1 className="page-title">Tax Collectors</h1>
          <p className="page-subtitle">
            Reusable collector identities — treasurers, tax claim bureaus, and third-party agencies referenced by jurisdiction assignments.
          </p>
          </div>
        </div>
        <div className="page-header-right">
          {canWrite && (
            <Button onClick={openCreate} data-testid="add-collector-button">
              <Plus className="w-4 h-4" />
              New Collector
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {kpiError && (
        <div className="jx-kpi-error" role="alert" data-testid="tax-collectors-kpi-error">
          Collector totals could not be loaded. The counts below are unavailable until the page is refreshed.
        </div>
      )}
      <div className="kpi-grid" data-testid="tax-collectors-kpi-grid">
        <div className="kpi-card kpi-accent-indigo" data-testid="kpi-total-collectors">
          <p className="kpi-label">Total Collectors</p>
          <div className="kpi-value" data-kpi-state={kpiError ? "error" : (kpiLoaded ? "ready" : "loading")}>{kpiValue(kpis.total)}</div>
          <p className="kpi-hint">All collector records</p>
        </div>
        <div className="kpi-card kpi-accent-emerald" data-testid="kpi-active-collectors">
          <p className="kpi-label">Active</p>
          <div className="kpi-value" data-kpi-state={kpiError ? "error" : (kpiLoaded ? "ready" : "loading")}>{kpiValue(kpis.active)}</div>
          <p className="kpi-hint">Available for assignments</p>
        </div>
        <div className="kpi-card kpi-accent-slate" data-testid="kpi-inactive-collectors">
          <p className="kpi-label">Inactive</p>
          <div className="kpi-value" data-kpi-state={kpiError ? "error" : (kpiLoaded ? "ready" : "loading")}>{kpiValue(kpis.inactive)}</div>
          <p className="kpi-hint">Deactivated / archived</p>
        </div>
        <div className="kpi-card kpi-accent-sky" data-testid="kpi-states-covered">
          <p className="kpi-label">States Covered</p>
          <div className="kpi-value" data-kpi-state={kpiError ? "error" : (kpiLoaded ? "ready" : "loading")}>{kpiValue(kpis.statesCovered)}</div>
          <p className="kpi-hint">Distinct states served</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input-wrap">
          <Search className="search-icon w-4 h-4" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or legal name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="tax-collector-search-input"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="filter-select"
          data-testid="tax-collector-type-filter"
        >
          <option value="all">All Types</option>
          {types.map(t => <option key={t} value={t}>{humanizeCollectorType(t)}</option>)}
        </select>

        <select
          value={activeFilter}
          onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
          className="filter-select"
          data-testid="tax-collector-active-filter"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        {hasFilters && (
          <Button variant="outline" onClick={resetFilters} data-testid="tax-collector-reset-filters">
            <X className="w-4 h-4" />
            Reset
          </Button>
        )}
      </div>

      {/* Stats */}
      {!loading && (
        <div className="stats-bar">
          <span className="stat-item">
            <strong>{pagination.total ?? items.length}</strong>{' '}
            {(pagination.total ?? items.length) === 1 ? 'collector' : 'collectors'}
            {hasFilters && <span> &nbsp;·&nbsp; filtered</span>}
          </span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading collectors…</p>
        </div>
      ) : noResults ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏢</div>
          <h3 className="empty-state-title">No collectors match your filters</h3>
          <p className="empty-state-description">
            {hasFilters
              ? 'Try adjusting your filters to see more results.'
              : 'Add your first tax collector to start linking jurisdictions.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="jurisdictions-table" data-testid="tax-collectors-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>States Served</th>
                <th style={{ textAlign: 'right' }}># Assignments</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(row => (
                <tr key={row.id} data-testid={`tax-collector-row-${row.id}`}>
                  <td>
                    <div className="jurisdiction-name" title={row.display_name}>{displayTitleCase(row.display_name)}</div>
                    {row.legal_name && row.legal_name !== row.display_name && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }} title={row.legal_name}>{displayTitleCase(row.legal_name)}</div>
                    )}
                  </td>
                  <td>
                    <span className={`type-badge ${collectorTypeBadgeClass(row.collector_type)}`}>
                      {humanizeCollectorType(row.collector_type)}
                    </span>
                  </td>
                  <td><StatesPill states={row.states_served} /></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>
                    {row.assignments_count ?? 0}
                  </td>
                  <td>
                    {row.is_active
                      ? <span className="status-badge status-active">Active</span>
                      : <span className="status-badge status-inactive">Inactive</span>}
                  </td>
                  <td style={{ fontSize: '13px', color: '#6b7280' }} title={row.updated_at || ''}>
                    <div>{relTime(row.updated_at || row.created_at)}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {row.updated_by_name || row.created_by_name || row.updated_by || row.created_by || '—'}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canWrite ? (
                      <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}
                          data-testid={`edit-collector-${row.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)}
                          data-testid={`delete-collector-${row.id}`}
                          className="text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : <span style={{ fontSize: '12px', color: '#94a3b8' }}>read only</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.total_pages > 1 && (
            <div className="pagination">
              <span className="stat-item">
                {pagination.total} total · page {page} of {pagination.total_pages}
              </span>
              <div className="pagination-controls">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Button variant="outline" size="sm" disabled={page >= pagination.total_pages}
                  onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="max-w-3xl p-0 flex flex-col max-h-[85vh]"
          data-testid="collector-form-dialog"
        >
          {/* Header — fixed, never scrolls */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
            <DialogTitle>{editing ? 'Edit Tax Collector' : 'New Tax Collector'}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Reusable collector identity. Used in jurisdiction assignments and rendered on certs.
            </DialogDescription>
          </DialogHeader>

          {/* Body — the ONLY scroll container. flex-1 fills the gap
              between fixed header & footer; min-h-0 lets it actually
              shrink instead of pushing the footer off-screen. */}
          <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <Field label="Display Name *">
              <Input value={draft.display_name}
                onChange={e => setDraft(d => ({ ...d, display_name: e.target.value }))}
                onBlur={handleBlurUpper('display_name')}
                data-testid="collector-display-name" />
            </Field>
            <Field label="Legal Name">
              <Input value={draft.legal_name || ''}
                onChange={e => setDraft(d => ({ ...d, legal_name: e.target.value }))}
                onBlur={handleBlurUpper('legal_name')} />
            </Field>
            <Field label="Type *">
              <Select value={draft.collector_type}
                onValueChange={v => setDraft(d => ({ ...d, collector_type: v }))}>
                <SelectTrigger data-testid="collector-type-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  {types.map(t => <SelectItem key={t} value={t}>{humanizeCollectorType(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Make Checks Payable To *" error={payableError}>
              <Input
                value={draft.make_checks_payable_to || ''}
                onChange={e => setDraft(d => ({ ...d, make_checks_payable_to: e.target.value.toUpperCase() }))}
                onBlur={handleBlurUpper('make_checks_payable_to')}
                aria-invalid={!!payableError || undefined}
                className={payableError ? 'border-rose-400 focus-visible:ring-rose-300' : ''}
                data-testid="collector-payable-to-input" />
            </Field>
            <Field label="Phone" error={phoneError}>
              <Input
                value={draft.phone || ''}
                onChange={handlePhoneChange('phone')}
                placeholder="302-289-6219"
                inputMode="tel"
                maxLength={12}
                aria-invalid={!!phoneError || undefined}
                className={phoneError ? 'border-rose-400 focus-visible:ring-rose-300' : ''}
                data-testid="collector-phone-input" />
            </Field>
            <Field label="Phone Extension">
              <Input value={draft.phone_ext || ''}
                onChange={e => setDraft(d => ({ ...d, phone_ext: e.target.value }))}
                onBlur={handleBlurTrim('phone_ext')} />
            </Field>
            <Field label="Fax" error={faxError}>
              <Input
                value={draft.fax || ''}
                onChange={handlePhoneChange('fax')}
                placeholder="302-289-6220"
                inputMode="tel"
                maxLength={12}
                aria-invalid={!!faxError || undefined}
                className={faxError ? 'border-rose-400 focus-visible:ring-rose-300' : ''}
                data-testid="collector-fax-input" />
            </Field>
            <Field label="Email">
              <Input type="email" value={draft.email || ''}
                onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
                onBlur={handleBlurTrim('email')} />
            </Field>
            <Field label="Website">
              <Input value={draft.website || ''}
                onChange={e => setDraft(d => ({ ...d, website: e.target.value }))}
                onBlur={handleBlurTrim('website')} />
            </Field>
            <Field label="Working Hours">
              <Input value={draft.working_hours || ''}
                onChange={e => setDraft(d => ({ ...d, working_hours: e.target.value }))}
                onBlur={handleBlurTrim('working_hours')} />
            </Field>
            <Field label="Mailing Address" full>
              <Textarea rows={2} value={draft.mailing_address || ''}
                onChange={e => setDraft(d => ({ ...d, mailing_address: e.target.value }))}
                onBlur={handleBlurUpper('mailing_address')} />
            </Field>
            <Field label="Physical Address *" full error={physicalError}>
              <Textarea rows={2} value={draft.physical_address || ''}
                onChange={e => setDraft(d => ({ ...d, physical_address: e.target.value }))}
                onBlur={handleBlurUpper('physical_address')}
                aria-invalid={!!physicalError || undefined}
                className={physicalError ? 'border-rose-400 focus-visible:ring-rose-300' : ''}
                data-testid="collector-physical-address-input" />
            </Field>
            <Field label="States Served (comma or pipe separated, e.g. PA, NJ)" full>
              <Input
                value={(draft.states_served || []).join(', ')}
                onChange={e => setDraft(d => ({
                  ...d,
                  states_served: e.target.value
                    .split(/[,|]/).map(s => s.trim().toUpperCase()).filter(Boolean)
                }))}
                data-testid="collector-states-input" />
            </Field>
            <Field label="Notes" full>
              <Textarea rows={3} value={draft.notes || ''}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                onBlur={handleBlurTrim('notes')} />
            </Field>
            {/* ── Operational Defaults panel (Feb 2026) ────────────────
                Surfaced read-only on the order — processors cannot edit
                these inline. Master data only. */}
            <div
              className="col-span-2 p-3 rounded-md border-2 border-dashed border-emerald-300 bg-emerald-50/30 space-y-3"
              data-testid="collector-operational-defaults-panel"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">⊕ Operational Defaults</div>
                <span className="text-[10px] text-emerald-700 italic">Used when this collector is linked to a jurisdiction without its own override</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Default MLC Fee ($)">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 50.00"
                    value={draft.default_mlc_fee ?? ''}
                    onChange={e => setDraft(d => ({
                      ...d,
                      default_mlc_fee: e.target.value === '' ? null : parseFloat(e.target.value),
                    }))}
                    data-testid="collector-default-mlc-fee"
                  />
                </Field>
                <Field label="Default ETA (days)">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="e.g. 5"
                    value={draft.default_eta_days ?? ''}
                    onChange={e => setDraft(d => ({
                      ...d,
                      default_eta_days: e.target.value === '' ? null : parseInt(e.target.value, 10),
                    }))}
                    data-testid="collector-default-eta-days"
                  />
                </Field>
                <Field label="Default SASE Required">
                  <Select
                    value={draft.default_sase_required === true ? 'yes' : draft.default_sase_required === false ? 'no' : 'unset'}
                    onValueChange={v => setDraft(d => ({
                      ...d,
                      default_sase_required: v === 'yes' ? true : v === 'no' ? false : null,
                    }))}>
                    <SelectTrigger data-testid="collector-default-sase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">(unset)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Request Instructions" full>
                <Textarea
                  rows={3}
                  placeholder="Cover-letter language, required attachments, phone-screen script, payment instructions, etc."
                  value={draft.request_instructions || ''}
                  onChange={e => setDraft(d => ({ ...d, request_instructions: e.target.value }))}
                  onBlur={handleBlurTrim('request_instructions')}
                  data-testid="collector-request-instructions"
                />
              </Field>
              <div className="flex items-start gap-2 px-2.5 py-2 rounded bg-amber-50 border border-amber-200">
                <svg className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
                <p className="text-[11px] text-amber-900">
                  <span className="font-semibold">Snapshot rule:</span> changing these values affects only future Mail-Away requests. Already-sent requests, completed orders, and historical snapshots keep the values they were created with.
                </p>
              </div>
            </div>
            <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-slate-100">
              <Switch checked={!!draft.accepts_mail_away}
                onCheckedChange={v => setDraft(d => ({ ...d, accepts_mail_away: v }))}
                id="accepts-mail-away" />
              <Label htmlFor="accepts-mail-away" className="text-sm">Accepts mail-away payments</Label>
              <div className="ml-auto flex items-center gap-3">
                <Switch checked={!!draft.is_active}
                  onCheckedChange={v => setDraft(d => ({ ...d, is_active: v }))}
                  id="is-active" />
                <Label htmlFor="is-active" className="text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-200 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setModalOpen(false)}
              data-testid="collector-cancel-button">Cancel</Button>
            <Button onClick={onSave} disabled={saving}
              data-testid="collector-save-button">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="collector-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete or deactivate this collector?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.display_name}: if any jurisdiction has an active assignment
              referencing this collector, it will be soft-deactivated; otherwise it will be
              permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="collector-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete}
              data-testid="collector-delete-confirm-button"
              className="bg-rose-600 hover:bg-rose-700">
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, full = false, error = '' }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-slate-500 mb-1.5 block">{label}</Label>
      {children}
      {error && (
        <p className="mt-1 text-xs text-rose-600" data-testid="field-error">
          {error}
        </p>
      )}
    </div>
  );
}
